import Accordion from "@/common/components/Accordion";
import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { ChangeHistory } from "@/common/components/ChangeHistory";
import GoBackButton from "@/common/components/GoBackButton";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { ResentContactEmailModal } from "@/common/components/ResentContactEmailModal";
import { useGoBack } from "@/common/hooks/useGoBack";
import { formatCurrency, formatPercentage } from '@/common/utils/formatters';
import { FormTransformers } from "@/common/utils/formTransformers";
import { CmContractInvoiceDetails } from "@/features/establishmentLeadsFunnel/components/CmContractInvoiceDetails";
import { ContractFeesSection } from "@/features/establishmentLeadsFunnel/components/ContractFeesSection";
import { ContractInvoiceDetails } from "@/features/establishmentLeadsFunnel/components/ContractInvoiceDetails";
import { RequestedReview } from "@/features/establishmentLeadsFunnel/components/RequestedReview";
import { stateStrategy, translateStateStrategy } from "@/features/establishmentLeadsFunnel/constants/stateStrategy.constant";
import { useAdvanceEstablishmentLead } from "@/features/establishmentLeadsFunnel/hooks/useAdvanceEstablishmentLead";
import { useUpdateEstablishmentLeadContract } from "@/features/establishmentLeadsFunnel/hooks/useUpdateEstablishmentLeadContract";
import { EstablishmentLeadsFunnelUpdateContractValidationSchema, updateContractSchema } from "@/features/establishmentLeadsFunnel/schemas/update.schema";
import { EstablishmentLeadContractResponseTransformers } from "@/features/establishmentLeadsFunnel/services/establishmentLeadContractTransformers";
import { EstablishmentLeadData } from "@/features/establishmentLeadsFunnel/types/establishmentLead.type";
import { Checklist } from "@/features/privateCompanyLeadsFunnel/components/Checklist";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Building2Icon } from "lucide-react";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

const Form = ({
  establishmentLead,
}: {
  establishmentLead: EstablishmentLeadData,
}) => {
  const { establishmentLeadId } = useParams()
  const goBack = useGoBack()
  const queryClient = useQueryClient()

  const contract = establishmentLead.contract
  const maintenanceContract = establishmentLead.maintenance_contract
  const isNotQualifyingQuote = useMemo(() => establishmentLead.state !== 'qualifying_quote', [establishmentLead.state])
  const isInQualifyingQuoteClientReview = useMemo(() => establishmentLead.state === 'qualifying_quote_client_review', [establishmentLead.state])
  const passedEveryRequirement = establishmentLead?.requirement_list_summary?.steps?.every((step) => step.passed)

  const onSuccess = () => {
    clearErrors();
    queryClient.invalidateQueries({ queryKey: ['establishmentLead', establishmentLead.id] })
  }

  const onSuccessRedirect = () => {
    queryClient.invalidateQueries({ queryKey: ['establishmentLead', establishmentLead.id] })
    goBack({ fallback: '/establishment-leads' })
  }

  const {
    mutate: updateContract,
    isError: updateContractIsError,
  } = useUpdateEstablishmentLeadContract({
    establishmentLeadId,
    establishmentLead,
    onSuccess,
    onError: (error) => {
      FormTransformers.errorsTransformer({
        setError,
        transformer: EstablishmentLeadContractResponseTransformers.errorsTransformer()
      })(error)
    },
  })

  const {
    mutate: advanceEstablishmentLead,
    isPending: advanceEstablishmentLeadIsPending,
    isError: advanceEstablishmentLeadIsError,
  } = useAdvanceEstablishmentLead({
    id: establishmentLeadId,
    onSuccess: onSuccessRedirect,
  })

  const isServerError = (updateContractIsError || advanceEstablishmentLeadIsError)

  const {
    control,
    setValue,
    getValues,
    setError,
    clearErrors,
    watch,
    formState: { errors }
  } = useForm<EstablishmentLeadsFunnelUpdateContractValidationSchema>({
    resolver: zodResolver(updateContractSchema),
    defaultValues: {
      contract_fees: establishmentLead.contract?.contract_fees?.map((contractFee) => ({
        id: contractFee.id,
        fee_template: {
          id: contractFee.fee_template.id,
          display_name: contractFee.fee_template.display_name,
          calculate_as: contractFee.fee_template.calculate_as,
        },
        value: contractFee.fee_template.calculate_as === 'percentage' ? formatPercentage(contractFee.value) : formatCurrency(contractFee.value),
        main_fee: contractFee.main_fee,
      })) || []
    }
  })

  const submit = () => {
    const data = getValues()

    updateContract(data)
  }

  return (
    <form>
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2 items-center">
          <GoBackButton
            fallback={`/establishment-leads/${establishmentLeadId}/edit`}
          />

          <ImageWithPlaceholder
            url={establishmentLead?.general_information?.company_logo?.url}
            placeholderIcon={Building2Icon}
            className="size-10"
            iconClassName="size-7"
          />

          <p className="text-white-400 font-normal">
            {establishmentLead.display_name}
          </p>
        </div>

        <div className="flex gap-2">
          <div className="flex gap-2">
            {!isNotQualifyingQuote ? (
              <Button
                type="button"
                className={passedEveryRequirement ? "shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" : ''}
                variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                disabled={!passedEveryRequirement || advanceEstablishmentLeadIsPending}
                onClick={() => advanceEstablishmentLead()}
              >
                Enviar condição comercial para clientes
              </Button>
            ) : isInQualifyingQuoteClientReview ? (
              <ResentContactEmailModal contacts={establishmentLead.contacts || []} />
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex gap-6 w-full">
        <Box className="flex w-full flex-col gap-4 h-[calc(100vh-128px)] overflow-y-auto shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          {isServerError && (
            <p className="text-sm text-danger-soft">
              Ocorreu um erro no servidor, cheque os campos e tente novamente
            </p>
          )}

          {contract?.last_review?.state === 'disapproved' && (
            <RequestedReview description={contract?.last_review?.description} />
          )}

          <div className="flex gap-2 items-center">
            <GoBackButton
              fallback={`/establishment-leads/${establishmentLeadId}/edit`}
            />

            <div className="flex flex-col">
              <p className="font-bold text-lg">
                Condições comerciais <span className="font-light text-sm italic">{contract?.protocol_number}</span>
              </p>
            </div>
          </div>

          <Accordion
            className="p-0"
            defaultOpen
            triggerClassName="p-4"
            contentClassName="p-4 pt-0"
            head="Controle de abastecimento (CA)"
            body={(
              <div className="flex flex-col gap-4">
                <ContractInvoiceDetails
                  contract={contract}
                  control={control}
                  submit={submit}
                  watch={watch}
                  setValue={setValue}
                  disableFields={isNotQualifyingQuote}
                  errors={errors}
                />

                <ContractFeesSection
                  establishmentLeadId={establishmentLeadId}
                  contract={contract}
                  control={control}
                  watch={watch}
                  errors={errors}
                  disableFields={isNotQualifyingQuote}
                />
              </div>
            )}
          />

          <Accordion
            className="p-0"
            defaultOpen
            triggerClassName="p-4"
            contentClassName="p-4 pt-0"
            head="Controle de manutenção (CM)"
            body={(
              <div className="flex flex-col gap-4">
                <CmContractInvoiceDetails
                  maintenanceContract={maintenanceContract}
                  control={control}
                  submit={submit}
                  watch={watch}
                  setValue={setValue}
                  disableFields={isNotQualifyingQuote}
                  errors={errors}
                />
              </div>
            )}
          />
        </Box>

        <div className="flex flex-col gap-4">
          <Checklist
            requirement_list_summary={establishmentLead.requirement_list_summary}
            state={translateStateStrategy[stateStrategy[establishmentLead.state as keyof typeof stateStrategy]]}
            className="flex flex-col gap-2 justify-between h-[calc((100vh-276px)/2)]"
            Buttons={() => (
              <>
                {!isNotQualifyingQuote ? (
                  <Button
                    type="button"
                    variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                    disabled={!passedEveryRequirement || advanceEstablishmentLeadIsPending}
                    onClick={() => advanceEstablishmentLead()}
                  >
                    Enviar condição comercial
                  </Button>
                ) : isInQualifyingQuoteClientReview ? (
                  <ResentContactEmailModal contacts={establishmentLead.contacts || []} />
                ) : null}
              </>
            )}
          />

          <ChangeHistory
            logs={establishmentLead.logs || []}
            entityId={establishmentLead.id}
          />
        </div>
      </div>
    </form>
  )
}

export default Form
